import { useMemo, useState } from "react";
import type { HousingRelationshipAnalysis } from "../data/housingRelationshipAnalysis.ts";
import type { DirectionalTransmissionEvidence, DirectionalityVerdict } from "../data/transmissionEngine.ts";
import type { HypothesisResult, EvidenceVerdict, HypothesisVerdict } from "../data/hypothesisEngine.ts";
import type { DiscoveryKind } from "../data/discoveryEngine.ts";
import type { LocalProjectionResult, ProjectionVerdict } from "../data/localProjectionEngine.ts";
import type { CompositionDefenseStatus } from "../data/marketStateEngine.ts";

function r(value: number | null) {
  return value == null ? "N/A" : value.toFixed(2);
}

function pct(value: number) {
  return `${Math.round(value * 100)}%`;
}

function directionLabel(value: DirectionalityVerdict) {
  const map: Record<DirectionalityVerdict, string> = {
    forward_dominant: "정방향 우세",
    reverse_dominant: "역방향 우세",
    bidirectional: "양방향",
    contemporaneous: "같은 달 동행",
    weak: "관계 약함",
    insufficient: "판정 대기"
  };
  return map[value];
}

function hypothesisLabel(value: HypothesisVerdict) {
  return value === "supported" ? "지지" : value === "partially_supported" ? "부분 지지" : value === "contradicted" ? "반박 신호" : "판단 유보";
}

function edgeLabel(value: EvidenceVerdict) {
  return value === "supported" ? "지지" : value === "partial" ? "부분" : value === "counterevidence" ? "반대" : value === "weak" ? "약함" : "N/A";
}

function discoveryLabel(kind: DiscoveryKind) {
  return kind === "reverse_causality_risk" ? "역인과 위험"
    : kind === "bidirectional_loop" ? "양방향 루프"
      : kind === "forward_path_candidate" ? "정방향 후보"
        : kind === "contemporaneous_only" ? "동시반응"
          : kind === "null_sensitive" ? "우연 lag 위험"
            : kind === "control_sensitive_response" ? "통제 민감"
              : kind === "lp_response_candidate" ? "조정 반응후보"
                : kind === "market_state_divergence" ? "가격·거래량 괴리"
                  : kind === "rate_asymmetry" ? "인상·인하 비대칭"
                    : kind === "rate_level_interaction_candidate" ? "금리수준 상태의존"
                      : kind === "hysteresis_candidate" ? "경로의존성"
                      : kind === "rate_threshold_candidate" ? "절대금리 임계점"
                      : kind === "spatial_lead_candidate" ? "지역 선행 후보"
                        : kind === "market_state_change_point" ? "시장상태 구조변화"
                      : kind === "policy_interaction_candidate" ? "정책상태 의존"
                        : "반응구간 후보";
}

function projectionLabel(value: ProjectionVerdict) {
  return value === "robust_candidate" ? "조정 후 반응 유지" : value === "control_sensitive" ? "공통요인 통제 민감" : value === "weak" ? "반응 약함" : "표본 부족";
}

function sensitivityLabel(value: LocalProjectionResult["controlSensitivity"]) {
  return value === "low" ? "낮음" : value === "medium" ? "중간" : value === "high" ? "높음" : "N/A";
}

function effect(value: number | null) {
  return value == null ? "N/A" : `${value >= 0 ? "+" : ""}${value.toFixed(2)}`;
}

function timing(evidence: DirectionalTransmissionEvidence) {
  const start = evidence.timing.concentrationStartLagMonths;
  const end = evidence.timing.concentrationEndLagMonths;
  if (start == null || end == null) return "N/A";
  return start === end ? `${start}개월` : `${start}~${end}개월`;
}


function marketStateLabel(value: string) {
  const map: Record<string, string> = {
    broad_strength: "거래 동반 강세",
    thin_market_strength: "거래감소 속 가격강세",
    capitulation_price_discovery: "거래증가 속 가격하락",
    frozen_weakness: "거래동결·가격약세",
    price_flat: "가격 보합",
    volume_flat: "거래량 보합",
    mixed_flat: "작은 변화",
    insufficient: "판정 대기"
  };
  return map[String(value)] ?? String(value);
}

function priceBasisLabel(value: HousingRelationshipAnalysis["marketStates"]["results"][number]["priceBasis"]) {
  return value === "official_index" ? "공식 가격지수"
    : value === "rtms_fixed_cell" ? "RTMS fixed-cell"
      : "RTMS 거래가격 proxy";
}

function compositionDefenseLabel(value: CompositionDefenseStatus) {
  return value === "same_direction" ? "원 proxy와 같은 방향"
    : value === "opposite_direction" ? "원 proxy와 반대 방향"
      : value === "fixed_cell_only" ? "fixed-cell만 판정"
        : "해당 없음";
}

function confidenceLabel(value: "high" | "medium" | "low" | "insufficient") {
  return value === "high" ? "높음" : value === "medium" ? "보통" : value === "low" ? "낮음" : "N/A";
}

function asymmetryLabel(value: HousingRelationshipAnalysis["nonlinearTransmission"]["asymmetry"][number]["verdict"]) {
  return value === "hike_stronger" ? "인상기 관계가 더 강함"
    : value === "cut_stronger" ? "인하기 관계가 더 강함"
      : value === "roughly_symmetric" ? "대체로 대칭"
        : value === "mixed" ? "혼재"
          : "판정 대기";
}

function rateLevelInteractionLabel(value: HousingRelationshipAnalysis["nonlinearTransmission"]["rateLevelInteraction"][number]["verdict"]) {
  return value === "high_level_stronger" ? "상대 고금리에서 더 강한 방향 후보"
    : value === "low_level_stronger" ? "상대 저금리에서 더 강한 방향 후보"
      : value === "control_sensitive" ? "통제 민감"
        : value === "no_clear_difference" ? "뚜렷한 차이 없음"
          : "표본 부족";
}

function hysteresisLabel(value: HousingRelationshipAnalysis["hysteresis"]["results"][number]["verdict"]) {
  return value === "hysteresis_candidate" ? "경로의존성 후보" : value === "no_clear_difference" ? "뚜렷한 차이 없음" : "표본 부족";
}

function thresholdKinkLabel(value: HousingRelationshipAnalysis["rateThresholdKink"]["results"][number]["verdict"]) {
  return value === "robust_threshold_candidate" ? "보정 후 임계점 후보"
    : value === "no_robust_threshold" ? "보정 후 견고한 임계점 없음"
      : "표본 부족";
}

function spatialLeadLabel(value: HousingRelationshipAnalysis["spatialTransmission"]["results"][number]["verdict"]) {
  return value === "seoul_leads_gyeonggi_candidate" ? "서울 선행 후보"
    : value === "gyeonggi_leads_seoul_candidate" ? "경기 선행 후보"
      : value === "bidirectional_candidate" ? "양방향 후보"
        : value === "contemporaneous_only" ? "같은 달 동행"
          : value === "common_rate_sensitive" ? "공통금리 조정 민감"
            : value === "no_robust_lead" ? "견고한 선행 없음"
              : "표본 부족";
}

function changePointLabel(value: HousingRelationshipAnalysis["marketStateChangePoints"]["results"][number]["verdict"]) {
  return value === "robust_change_point_candidate" ? "보정 후 구조변화 후보"
    : value === "no_robust_change_point" ? "보정 후 견고한 변화 없음"
      : "표본 부족";
}

function changePointSignalLabel(value: HousingRelationshipAnalysis["marketStateChangePoints"]["results"][number]["robustCandidate"]) {
  if (!value?.signal) return "N/A";
  return value.signal === "both" ? "수치+상태구성" : value.signal === "numeric_shift" ? "가격·거래 수치" : "상태구성";
}

function readinessLabel(value: HousingRelationshipAnalysis["dataCapabilityMatrix"][number]["readiness"]) {
  return value === "current_data_ready" ? "현재 데이터로 가능"
    : value === "backfill_bound" ? "현재 API 백필 대기"
      : value === "forward_accumulation" ? "지금부터 축적"
        : "신규 소스 필요";
}

function HypothesisCard({ result }: { result: HypothesisResult }) {
  return (
    <article className={`hypothesis-card hypothesis-card--${result.verdict}`}>
      <header>
        <div><span>범용 가설 템플릿</span><strong>{result.title}</strong></div>
        <b>{hypothesisLabel(result.verdict)}</b>
      </header>
      <p className="hypothesis-claim">{result.claim}</p>
      <p>{result.explanation}</p>
      <div className="hypothesis-paths">
        {result.paths.map((path) => (
          <div className="hypothesis-path" key={path.id}>
            <div className="hypothesis-path__top"><strong>{path.label}</strong><span>{edgeLabel(path.verdict)} · 경로자료 {pct(path.coverage)}</span></div>
            <small>{path.description}</small>
            <div className="hypothesis-edge-list">
              {path.edges.map((edge) => (
                <div key={edge.edgeId}>
                  <span className={`hypothesis-edge-status hypothesis-edge-status--${edge.verdict}`}>{edgeLabel(edge.verdict)}</span>
                  <strong>{edge.label}</strong>
                  <small>정방향 r {r(edge.correlation)} · {edge.timingLabel} · {directionLabel(edge.directionality)}</small>
                  {edge.projectionVerdict && <small>LP {projectionLabel(edge.projectionVerdict)} · peak {edge.projectionPeakHorizonMonths ?? "N/A"}M · 통제민감도 {edge.controlSensitivity ? sensitivityLabel(edge.controlSensitivity) : "N/A"}</small>}
                  <p>{edge.reason}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <footer><span>신뢰도 {result.confidence === "high" ? "높음" : result.confidence === "medium" ? "보통" : result.confidence === "low" ? "낮음" : "N/A"}</span><small>{result.note}</small></footer>
    </article>
  );
}

export default function TransmissionHypothesisPanel({ analysis }: { analysis: HousingRelationshipAnalysis }) {
  const [selectedId, setSelectedId] = useState(() => analysis.directionalEvidence[0]?.id ?? "");
  const [identifiedId, setIdentifiedId] = useState(() => analysis.identifiedShockLocalProjections[0]?.id ?? "");
  const [marketStateId, setMarketStateId] = useState(() => analysis.marketStates.results[0]?.id ?? "");
  const [asymmetryId, setAsymmetryId] = useState(() => analysis.nonlinearTransmission.asymmetry[0]?.id ?? "");
  const selected = useMemo(() => analysis.directionalEvidence.find((item) => item.id === selectedId) ?? analysis.directionalEvidence[0] ?? null, [analysis.directionalEvidence, selectedId]);
  const projection = useMemo(() => analysis.localProjections.find((item) => item.id === selected?.id) ?? null, [analysis.localProjections, selected?.id]);
  const identifiedProjection = useMemo(() => analysis.identifiedShockLocalProjections.find((item) => item.id === identifiedId) ?? analysis.identifiedShockLocalProjections[0] ?? null, [analysis.identifiedShockLocalProjections, identifiedId]);
  const marketState = useMemo(() => analysis.marketStates.results.find((item) => item.id === marketStateId) ?? analysis.marketStates.results[0] ?? null, [analysis.marketStates.results, marketStateId]);
  const asymmetry = useMemo(() => analysis.nonlinearTransmission.asymmetry.find((item) => item.id === asymmetryId) ?? analysis.nonlinearTransmission.asymmetry[0] ?? null, [analysis.nonlinearTransmission.asymmetry, asymmetryId]);

  return (
    <section className="housing-hypothesis-section">
      <header className="housing-h4-header housing-structure-header">
        <div>
          <p className="kicker">H4.7 · DIRECTION / HYPOTHESIS / DISCOVERY</p>
          <h4>전파경로를 먼저 검증하고, 그 위에서 경제 가설을 평가</h4>
          <p>특정 주장 하나를 하드코딩하지 않습니다. 모든 edge에 같은 방향성 방어를 적용하고, edge를 조합해 여러 가설을 만들거나 시스템이 구조변화 후보를 자동 제안할 수 있게 했습니다.</p>
          <small>0M은 동행으로만 표시하고 전파속도에서 제외합니다. X→Y와 Y→X를 동시에 계산하며, 거래량은 YoY 변화로 계절성을 줄이고, 여러 lag 중 우연히 큰 r를 고르는 위험은 block-permutation 경계로 따로 표시합니다.</small>
        </div>
        {analysis.directionalEvidence.length > 0 && (
          <label className="housing-relationship-select">
            <span>edge 선택</span>
            <select value={selected?.id ?? ""} onChange={(event) => setSelectedId(event.target.value)}>
              {analysis.directionalEvidence.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
        )}
      </header>

      {selected ? (
        <>
          <div className="directionality-summary-grid">
            <article><span>방향 판정</span><strong>{directionLabel(selected.verdict)}</strong><p>정방향과 역방향을 같은 1~12개월 규칙으로 비교합니다.</p></article>
            <article><span>0개월 동행</span><strong>r {r(selected.contemporaneous.correlation)}</strong><p>같은 달의 선후관계는 알 수 없어 전파시차에 넣지 않습니다.</p></article>
            <article><span>정방향</span><strong>r {r(selected.forward.correlation)}</strong><p>{timing(selected)} 반응 집중 · peak {selected.timing.peakLagMonths ?? "N/A"}M</p></article>
            <article><span>역방향</span><strong>r {r(selected.reverse.correlation)}</strong><p>{selected.reverse.windowLabel ?? "N/A"} · 역인과/피드백 점검용</p></article>
            <article><span>우연 lag 방어</span><strong>{selected.nullEnvelope.maxAbsCorrelation95 == null ? "N/A" : `95% |r| ${selected.nullEnvelope.maxAbsCorrelation95.toFixed(2)}`}</strong><p>{selected.nullEnvelope.forwardPasses == null ? "표본 부족" : selected.nullEnvelope.forwardPasses ? "정방향이 경계 초과" : "정방향이 경계 미달"}</p></article>
            <article><span>변환</span><strong>{selected.leaderTransform} → {selected.followerTransform}</strong><p>변수 성격에 따라 계절성 방어를 다르게 적용합니다.</p></article>
          </div>
          {selected.warnings.length > 0 && <div className="directionality-warnings">{selected.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}</div>}
          <p className="housing-h4-footnote"><b>현재 해석:</b> {selected.timing.note} {selected.nullEnvelope.note}</p>
        </>
      ) : <div className="empty-state">방향성 분석을 만들 수 있는 edge가 아직 없습니다.</div>}

      <div className="subsection-heading hypothesis-heading lp-heading">
        <div><p className="kicker">H4.8 · LOCAL PROJECTION / CONTROLS</p><h3>같이 움직였다는 것에서 한 단계 더: 충격 뒤 시간경로와 공통요인 민감도</h3></div>
        <p>월별 shock의 1표준편차 변화 뒤 결과변수가 t-1 대비 어떻게 누적 반응하는지 0~12개월 Local Projection으로 봅니다. 자기시차와 lagged common factors를 넣은 조정반응을 원시반응과 비교합니다.</p>
      </div>
      {projection ? (
        <div className="lp-panel">
          <div className="lp-summary-grid">
            <article><span>조정 반응 판정</span><strong>{projectionLabel(projection.verdict)}</strong><p>방향성 r 하나가 아니라 여러 horizon의 누적반응이 이어지는지 봅니다.</p></article>
            <article><span>Shock 식별</span><strong>{projection.identification.appliedMethod === "residual_innovation_proxy" ? "잔차 innovation proxy" : "관측 shock"}</strong><p>{projection.shockScaleLabel}{projection.identification.residualShareOfObservedStd != null ? ` · 잔차변동 ${Math.round(projection.identification.residualShareOfObservedStd * 100)}%` : ""}</p></article>
            <article><span>공통요인 민감도</span><strong>{sensitivityLabel(projection.controlSensitivity)}</strong><p>원시반응과 lagged-control 조정반응의 부호·크기를 비교합니다.</p></article>
            <article><span>Peak 반응</span><strong>{projection.peakHorizonMonths == null ? "N/A" : `${projection.peakHorizonMonths}개월 · ${effect(projection.peakAdjustedEffect)}`}</strong><p>{projection.responseUnitLabel}</p></article>
            <article><span>연속 반응구간</span><strong>{projection.concentrationStartHorizonMonths == null || projection.concentrationEndHorizonMonths == null ? "N/A" : `${projection.concentrationStartHorizonMonths}~${projection.concentrationEndHorizonMonths}개월`}</strong><p>HAC 95% pointwise 구간이 같은 방향으로 연속해서 0을 벗어난 구간입니다.</p></article>
            <article><span>인과 준비도</span><strong>{projection.causalReadiness === "externally_identified" ? "외생 shock 연결" : "관찰적 분석"}</strong><p>현재 residual innovation은 금통위 surprise나 고빈도 외생 shock이 아닙니다.</p></article>
          </div>
          <div className="lp-key-horizons">
            {[0, 1, 3, 6, 12].map((horizon) => {
              const point = projection.points.find((item) => item.horizonMonths === horizon);
              if (!point) return null;
              return <article key={horizon}><span>{horizon}M</span><strong>{effect(point.adjustedEffect)}</strong><small>원시 {effect(point.baselineEffect)}</small><small>{point.adjustedCiLow95 == null || point.adjustedCiHigh95 == null ? "HAC CI N/A" : `95% ${effect(point.adjustedCiLow95)} ~ ${effect(point.adjustedCiHigh95)}`}</small></article>;
            })}
          </div>
          <details className="lp-details">
            <summary>0~12개월 전체 Local Projection 표와 통제변수</summary>
            <div className="lp-controls"><strong>lagged controls</strong>{projection.controls.length ? projection.controls.map((control) => <span key={control.id}>{control.label} (t-{control.lagMonths})</span>) : <span>추가 공통요인 없음</span>}</div>
            <div className="lp-table-wrap"><table className="lp-table"><thead><tr><th>h</th><th>원시반응</th><th>조정반응</th><th>HAC 95%</th><th>표본</th></tr></thead><tbody>{projection.points.map((point) => <tr key={point.horizonMonths}><td>{point.horizonMonths}M</td><td>{effect(point.baselineEffect)}</td><td>{effect(point.adjustedEffect)}</td><td>{point.adjustedCiLow95 == null || point.adjustedCiHigh95 == null ? "N/A" : `${effect(point.adjustedCiLow95)} ~ ${effect(point.adjustedCiHigh95)}`}</td><td>{point.adjustedSamples}</td></tr>)}</tbody></table></div>
          </details>
          <div className="directionality-warnings">{projection.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}</div>
          <p className="housing-h4-footnote"><b>Shock proxy:</b> {projection.identification.note}</p>
        </div>
      ) : <div className="empty-state">선택한 edge는 Local Projection 대상이 아니거나 36개월 최소표본을 아직 확보하지 못했습니다.</div>}

      <div className="subsection-heading hypothesis-heading lp-heading">
        <div><p className="kicker">H4.9 · SHOCK ID / PIT GUARD</p><h3>충격의 식별 등급과 당시시점 재현 가능성을 따로 검사</h3></div>
        <p>시장 재평가 proxy, 검증 외부 shock, PIT release-vintage 가용성을 같은 등급으로 섞지 않습니다. shock가 좋아도 결과변수의 당시 공개값이 없으면 과거 실시간 검증은 차단합니다.</p>
      </div>
      <div className="lp-summary-grid">
        <article><span>정책 이벤트 proxy</span><strong>{analysis.shockIdentification.policyEventProxy.matchedEvents} / {analysis.shockIdentification.policyEventProxy.detectedPolicyChanges}건 매칭</strong><p>기준금리 변경일 KTB1Y 같은 날 재평가 · event_proxy_only</p></article>
        <article><span>외부 shock 연결</span><strong>{analysis.shockIdentification.externalRegistry.eligible.length ? `${analysis.shockIdentification.externalRegistry.eligible.length}개 검증` : "없음"}</strong><p>{analysis.shockIdentification.externalRegistry.status} · 기본값은 causal 승격 없음</p></article>
        <article><span>PIT 상태</span><strong>인증 {analysis.pitReplayAudit.counts.certified} · proxy {analysis.pitReplayAudit.counts.proxy} · 차단 {analysis.pitReplayAudit.counts.blocked}</strong><p>관측별 availableAt/release-vintage 여부를 별도 점검합니다.</p></article>
        <article><span>주택 exact PIT</span><strong>{analysis.pitReplayAudit.housingExactReplayReady ? "가능" : "차단"}</strong><p>현재 최신 수정 history만 있으면 당시시점 백테스트로 표시하지 않습니다.</p></article>
      </div>
      {analysis.identifiedShockLocalProjections.length > 0 ? (
        <div className="lp-panel">
          <label className="housing-relationship-select">
            <span>식별 shock 경로</span>
            <select value={identifiedProjection?.id ?? ""} onChange={(event) => setIdentifiedId(event.target.value)}>
              {analysis.identifiedShockLocalProjections.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          {identifiedProjection && <>
            <div className="lp-summary-grid">
              <article><span>Shock 식별등급</span><strong>{identifiedProjection.causalReadiness === "externally_identified" ? "externally identified" : "event proxy"}</strong><p>{identifiedProjection.identification.appliedMethod}</p></article>
              <article><span>LP 판정</span><strong>{projectionLabel(identifiedProjection.verdict)}</strong><p>식별 shock라도 outcome/control PIT는 별도 gate입니다.</p></article>
              <article><span>Peak 반응</span><strong>{identifiedProjection.peakHorizonMonths == null ? "N/A" : `${identifiedProjection.peakHorizonMonths}개월 · ${effect(identifiedProjection.peakAdjustedEffect)}`}</strong><p>{identifiedProjection.responseUnitLabel}</p></article>
              <article><span>Shock PIT</span><strong>{identifiedProjection.identification.pointInTimeSafe ? "검증" : "미검증"}</strong><p>shock 자체의 availableAt 조건입니다.</p></article>
            </div>
            <p className="housing-h4-footnote"><b>식별 메모:</b> {identifiedProjection.identification.note}</p>
          </>}
        </div>
      ) : <div className="empty-state">현재 검증 외부 shock는 없으며, 정책 이벤트 proxy도 최소표본을 충족하지 못하면 LP를 만들지 않습니다. 이는 정상적인 fail-closed 상태입니다.</div>}
      <details className="lp-details">
        <summary>PIT as-of replay와 차단 사유 보기</summary>
        <div className="lp-table-wrap"><table className="lp-table"><thead><tr><th>시계열</th><th>등급</th><th>상태</th><th>설명</th></tr></thead><tbody>{analysis.pitReplayAudit.series.map((item) => <tr key={item.seriesId}><td>{item.label}</td><td>{item.mode}</td><td>{item.status}</td><td>{item.note}</td></tr>)}</tbody></table></div>
        {analysis.pitReplayAudit.edgeReplays.map((edge) => <div key={edge.id} className="housing-h4-footnote"><b>{edge.label}</b> · {edge.status} · as-of 판정 {edge.points.length}개<br/><small>{edge.note}</small></div>)}
        <p className="housing-h4-footnote"><b>핵심 경계:</b> {analysis.pitReplayAudit.note}</p>
      </details>

      <div className="subsection-heading hypothesis-heading lp-heading">
        <div><p className="kicker">H4.10 · MARKET STATE / NONLINEAR REGIME</p><h3>가격·거래 상태, RTMS 구성효과 방어, 인상·인하 비대칭, 상대 금리수준·경로의존성을 분리</h3></div>
        <p>가격↑·거래↓ 같은 divergence를 기록하되, 가격대별 RTMS는 동일 법정동·단지·전용면적 fixed-cell이 충분히 매칭될 때 그 변화를 우선 사용합니다. 매물량·현금매수·경매 자료가 없으면 원인을 공급잠김·현금부자·강제매도로 확정하지 않습니다.</p>
      </div>
      {marketState ? (
        <div className="lp-panel">
          <label className="housing-relationship-select">
            <span>시장상태 선택</span>
            <select value={marketState.id} onChange={(event) => setMarketStateId(event.target.value)}>
              {analysis.marketStates.results.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <div className="lp-summary-grid">
            <article><span>현재 상태</span><strong>{marketState.current ? (marketState.currentFresh ? marketStateLabel(marketState.current.state) : "최신 판정 대기") : "N/A"}</strong><p>{marketState.current?.month ?? "공통월 없음"}{marketState.current && !marketState.currentFresh && marketState.latestVolumeMonth ? ` 마지막 판정 · 최신 거래월 ${marketState.latestVolumeMonth}` : ""} · {marketState.currentRun ? `${marketState.currentRun.months}개월 연속${marketState.currentRun.persistent ? " · 지속상태" : " · 아직 미확정"}` : "flat/mixed 또는 공통월 부족"}<br/>{marketState.current?.note ?? "표본 부족"}</p></article>
            <article><span>가격 변화</span><strong>{marketState.current?.priceChangePct == null ? "N/A" : `${marketState.current.priceChangePct >= 0 ? "+" : ""}${marketState.current.priceChangePct.toFixed(2)}%`}</strong><p>{priceBasisLabel(marketState.priceBasis)}</p></article>
            <article><span>거래량 YoY</span><strong>{marketState.current?.volumeYoyLogChangePct == null ? "N/A" : `${marketState.current.volumeYoyLogChangePct >= 0 ? "+" : ""}${marketState.current.volumeYoyLogChangePct.toFixed(1)}%`}</strong><p>계절성 방어용 YoY 로그변화</p></article>
            <article><span>시장청산 신뢰도</span><strong>{marketState.current ? confidenceLabel(marketState.current.priceConfidence) : "N/A"}</strong><p>{marketState.current?.liquidityRatioToPrior12Median == null ? "직전 12M 비교 N/A" : `거래량/직전12M 중앙값 ${(marketState.current.liquidityRatioToPrior12Median * 100).toFixed(0)}%`}</p></article>
            {marketState.priceBasis === "rtms_fixed_cell" && <article><span>구성효과 방어</span><strong>{marketState.current ? compositionDefenseLabel(marketState.current.compositionDefense) : "N/A"}</strong><p>{marketState.current?.fixedCellMatchedCellCount == null ? "매칭셀 N/A" : `${marketState.current.fixedCellMatchedCellCount}개 셀 · 포착률 ${marketState.current.fixedCellCoveragePct?.toFixed(0) ?? "N/A"}%`}{marketState.current?.rawPriceProxyChangePct == null ? "" : ` · 원 proxy ${marketState.current.rawPriceProxyChangePct >= 0 ? "+" : ""}${marketState.current.rawPriceProxyChangePct.toFixed(2)}%`}</p></article>}
            <article><span>Divergence</span><strong>{marketState.divergenceEpisodes.length ? `${marketState.divergenceEpisodes.length}개 지속구간` : "지속구간 없음"}</strong><p>최소 2개 연속 완결월만 episode로 승격</p></article>
            <article><span>상태전이</span><strong>{marketState.transitions.length ? `${marketState.transitions.length}개 확정후보` : "뚜렷한 전이 없음"}</strong><p>{marketState.transitions.length ? (() => { const latest = marketState.transitions.at(-1)!; return `최근 ${marketStateLabel(latest.from)} → ${marketStateLabel(latest.to)} · ${latest.month} 확인`; })() : "양쪽 상태가 각각 2개월 이상 지속돼야 하며 flat/mixed 또는 월 누락을 가로질러 연결하지 않습니다."}</p></article>
          </div>
          {marketState.warnings.length > 0 && <div className="directionality-warnings">{marketState.warnings.map((warning) => <p key={warning}>⚠ {warning}</p>)}</div>}
          <p className="housing-h4-footnote"><b>현재 범위:</b> {marketState.note}</p>
        </div>
      ) : <div className="empty-state">공식 가격과 RTMS 완결월이 겹치는 시장상태를 아직 만들 수 없습니다.</div>}

      {asymmetry ? (
        <div className="lp-panel">
          <label className="housing-relationship-select">
            <span>인상/인하 비교</span>
            <select value={asymmetry.id} onChange={(event) => setAsymmetryId(event.target.value)}>
              {analysis.nonlinearTransmission.asymmetry.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <div className="lp-summary-grid">
            <article><span>비대칭 판정</span><strong>{asymmetryLabel(asymmetry.verdict)}</strong><p>인상과 인하를 같은 선형계수로 강제하지 않습니다.</p></article>
            <article><span>인상 충격</span><strong>r {r(asymmetry.hike.forward.correlation)}</strong><p>{directionLabel(asymmetry.hike.verdict)} · {timing(asymmetry.hike)}</p></article>
            <article><span>인하 충격</span><strong>r {r(asymmetry.cut.forward.correlation)}</strong><p>{directionLabel(asymmetry.cut.verdict)} · {timing(asymmetry.cut)}</p></article>
          </div>
          <p className="housing-h4-footnote">{asymmetry.note}</p>
        </div>
      ) : <div className="empty-state">금리 인상/인하 비대칭 분석대상이 없습니다.</div>}

      <div className="lp-summary-grid">
        {analysis.nonlinearTransmission.rateLevelInteraction.map((item) => (
          <article key={item.id}><span>{item.side === "hike" ? "금리인상" : "금리인하"} × 상대 금리수준</span><strong>{rateLevelInteractionLabel(item.verdict)}</strong><p>{item.coverageStartMonth ?? "N/A"} ~ {item.coverageEndMonth ?? "N/A"} · high/low shock {item.highShockMonths}/{item.lowShockMonths}개월<br/>{item.note}</p></article>
        ))}
        {analysis.nonlinearTransmission.policyInteraction.map((item) => (
          <article key={item.id}><span>정책강도 × 금리</span><strong>{item.status === "ready" ? "분석 가능" : item.status === "limited" ? "제한적" : "표본 대기"}</strong><p>{item.exactCoverageStartMonth ?? "N/A"} ~ {item.exactCoverageEndMonth ?? "N/A"} · exact {item.exactMonths}개월<br/>{item.note}</p></article>
        ))}
        {analysis.rateThresholdKink.results.map((item) => {
          const candidate = item.robustCandidate ?? item.strongestCandidate;
          return <article key={item.id}><span>절대 금리 threshold/kink</span><strong>{thresholdKinkLabel(item.verdict)}</strong><p>{candidate ? `${candidate.cutoffPct.toFixed(2)}% cutoff · 이하/초과 ${candidate.belowSamples}/${candidate.aboveSamples}개월 · 6M 중앙값 차이 ${effect(candidate.thresholdDifferencePct)}% · kink ${effect(candidate.kinkSlopeDifference)}` : "비교 가능한 cutoff 부족"}<br/>{item.note}</p></article>;
        })}
        {analysis.hysteresis.results.map((item) => (
          <article key={item.id}><span>동일 금리수준 경로의존성</span><strong>{hysteresisLabel(item.verdict)}</strong><p>{item.strongestBucket ? `${item.strongestBucket.rateBucketLabel} · 6M 가격반응 차이 ${effect(item.strongestBucket.differencePct)}%` : "비교 가능한 금리구간 부족"}<br/>{item.note}</p></article>
        ))}
      </div>

      <div className="subsection-heading hypothesis-heading lp-heading">
        <div><p className="kicker">H4.11 · SPATIAL LEAD-LAG</p><h3>서울이 경기를 먼저 움직이는가, 경기가 서울을 먼저 움직이는가</h3></div>
        <p>서울·경기 RTMS를 같은 정의로 비교합니다. 0개월은 동행으로 격리하고 1~12개월 양방향 lag를 동시에 탐색하며, 여러 lag 중 우연히 큰 값을 고르는 문제는 moving-block permutation으로 방어합니다. 공통 주담대금리 변화 조정 뒤에도 같은 방향이 남을 때만 공간 선행 후보로 표시합니다.</p>
      </div>
      <div className="lp-summary-grid">
        {analysis.spatialTransmission.results.map((item) => {
          const adjusted = item.rateAdjusted;
          const lead = item.verdict === "seoul_leads_gyeonggi_candidate" ? adjusted?.forward : item.verdict === "gyeonggi_leads_seoul_candidate" ? adjusted?.reverse : null;
          return <article key={item.id}><span>{item.metric === "transaction_volume" ? "거래량 spatial" : `${item.bandLabel ?? "가격대"} fixed-cell spatial`}</span><strong>{spatialLeadLabel(item.verdict)}</strong><p>{item.coverageStartMonth ?? "N/A"} ~ {item.coverageEndMonth ?? "N/A"} · 공통 {item.commonMonths}개월 · 금리조정 {item.adjustedMonths}개월{item.controlResidualShareMin == null ? "" : ` · 최소 잔차변동 ${(item.controlResidualShareMin * 100).toFixed(0)}%`}{lead ? ` · 조정 r ${r(lead.correlation)} · ${lead.windowLabel ?? "N/A"}` : ""}<br/>{item.note}</p></article>;
        })}
      </div>
      {!analysis.spatialTransmission.results.length && <div className="empty-state">서울·경기 동일정의 RTMS 공통표본이 없어 spatial lead-lag를 아직 만들 수 없습니다.</div>}
      <p className="housing-h4-footnote"><b>해석 경계:</b> {analysis.spatialTransmission.note}</p>

      <div className="subsection-heading hypothesis-heading lp-heading">
        <div><p className="kicker">H4.12 · MARKET-STATE CHANGE POINT</p><h3>눈에 띄는 한 달이 아니라 실제 상태구조가 바뀌었는가</h3></div>
        <p>기존 2개월 상태전이는 빠른 상태분류용입니다. 여기서는 각 연속 완결구간에서 가능한 모든 break month를 나누어 전·후 가격·거래 평균과 시장상태 구성분포를 비교하고, 가장 큰 변화가 moving-block permutation 최대통계량 95% 경계를 넘는지 사후 검증합니다.</p>
      </div>
      <div className="lp-summary-grid">
        {analysis.marketStateChangePoints.results.map((item) => {
          const candidate = item.robustCandidate ?? item.strongestCandidate;
          return <article key={item.id}><span>{item.label}</span><strong>{changePointLabel(item.verdict)}</strong><p>{candidate ? `${candidate.changePointMonth} 후보 · ${candidate.confirmedAtMonth}까지 관측 후 확인 · ${changePointSignalLabel(item.robustCandidate)} · 전 ${candidate.beforeMonths}M/후 ${candidate.afterMonths}M` : `연속표본 최대 ${item.longestContiguousMonths}개월 · 후보 없음`}{candidate ? ` · 상태 ${marketStateLabel(candidate.dominantStateBefore)} → ${marketStateLabel(candidate.dominantStateAfter)}` : ""}<br/>{item.note}</p></article>;
        })}
      </div>
      {!analysis.marketStateChangePoints.results.length && <div className="empty-state">공식 가격지수 또는 fixed-cell 가격과 RTMS 거래량의 연속 공통표본이 없어 change-point를 만들 수 없습니다.</div>}
      <p className="housing-h4-footnote"><b>해석 경계:</b> {analysis.marketStateChangePoints.note}</p>

      <details className="analysis-capability-details">
        <summary>현재 데이터/API로 해결되는 것과 신규 소스가 필요한 것</summary>
        <div>{analysis.dataCapabilityMatrix.map((item) => <span key={item.id}><b>{readinessLabel(item.readiness)}</b> · {item.label} — {item.note}</span>)}</div>
      </details>

      <div className="subsection-heading hypothesis-heading">
        <div><p className="kicker">HYPOTHESIS GRAPH</p><h3>주장을 하나의 문장이 아니라 경로별 증거지도로 분해</h3></div>
        <p>각 가설은 edge 목록만 바꾸면 재사용됩니다. 고가주택 직접경로는 하나의 예시일 뿐이며 시장금리→정책금리, 금융→대출, 경기 간접경로 등 다른 주장도 같은 evaluator를 사용합니다.</p>
      </div>
      <div className="hypothesis-grid">
        {analysis.hypotheses.length ? analysis.hypotheses.map((result) => <HypothesisCard key={result.id} result={result} />) : <div className="empty-state">현재 연결 가능한 가설 경로가 없습니다.</div>}
      </div>

      <div className="subsection-heading hypothesis-heading">
        <div><p className="kicker">DISCOVERY LAYER</p><h3>사용자가 묻지 않아도 먼저 보여줄 구조 후보</h3></div>
        <p>역방향 우세, 양방향 루프, 0M 동행, null 민감도, 정방향 반응구간을 자동 순위화합니다. 발견 자체를 원인 확정으로 승격하지 않습니다.</p>
      </div>
      <div className="discovery-grid">
        {analysis.discoveries.length ? analysis.discoveries.map((item) => (
          <article key={item.id} className={`discovery-card discovery-card--${item.kind}`}>
            <span>{discoveryLabel(item.kind)}</span><strong>{item.title}</strong><p>{item.summary}</p><small>{item.caution}</small>
          </article>
        )) : <div className="empty-state">현재 우선 표시할 구조 후보가 없습니다.</div>}
      </div>

      <details className="analysis-capability-details">
        <summary>현재 구현된 방어장치와 아직 구현하지 않은 것</summary>
        <div><strong>이번 구현</strong>{analysis.capabilities.implemented.map((item) => <span key={item}>✓ {item}</span>)}</div>
        <div><strong>2단계 대기</strong>{analysis.capabilities.pending.map((item) => <span key={item}>○ {item}</span>)}</div>
        <p>외부 surprise source는 아직 미연결입니다. ECOS 월간/주택가격은 v17.3부터 변경시점 snapshot을 앞으로 축적하지만, archive 시작 이전의 과거 exact release-vintage는 복원되지 않으며 causal/PIT 완료로 자동 승격하지 않습니다.</p>
      </details>
    </section>
  );
}
